import {
  Box,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { themeOrange, themeBlue } from "../../../utils/colorTheme";
import Input from "../../../component/common/Input";
import { BsFillEyeFill, BsFillEyeSlashFill } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { asyncStatus } from "../../../utils/asyncStatus";
import { Form, Formik } from "formik";
import { vendorValidationsSchema } from "../../../utils/ValidationsSchema";
import CustomSelectBox from "../../../component/common/selectComp/CustomSelectBox";
import { setAddVendorStatus } from "../../../store/slices/user_auth_slice";
import { getUserByIdAsync, updateUserByIdAsync } from "../../../services/DashboardUsers";
import { addVendorAsync, get_signup_services } from "../../../services/authentication";
import { setAsyncStatusIDLE } from "../../../store/slices/dashboard_users_slice";
import { IoArrowBack } from "react-icons/io5";
import ButtonComp from "../../../component/common/ButtonComp";

const AddVendor = () => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const formikRef = useRef(null);
  const [showPass, setShowPass] = useState({ password: false, confirmPassword: false });
  const [initialValues, setInitialValues] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    services: [],
    cities: [],
  });

  const { add_vendor_status, get_services_for_signup_data } = useSelector(
    (state) => state.userAuth
  );
  const { get_user_by_id_data, update_user_status } = useSelector(
    (state) => state.dashboard_users || {}
  );

  const isLoading = add_vendor_status === asyncStatus.LOADING || update_user_status === asyncStatus.LOADING;

  useEffect(() => {
    dispatch(get_signup_services({ services: "yes", city: "yes" }));
    if (isEditMode) dispatch(getUserByIdAsync(id));
  }, [isEditMode, id]);

  useEffect(() => {
    if (isEditMode && get_user_by_id_data) {
      const vendorData = get_user_by_id_data;
      setInitialValues({
        first_name: vendorData?.first_name || "",
        last_name: vendorData?.last_name || "",
        email: vendorData?.email || "",
        phone: vendorData?.phone || "",
        password: "",
        confirmPassword: "",
        services: vendorData?.services?.map((s) => ({
          label: s.service?.name,
          value: s.service?.id,
        })) || [],
        cities: vendorData?.cities?.map((c) => ({
          label: c.city?.name,
          value: c.city?.id,
        })) || [],
      });
    }
  }, [get_user_by_id_data]);

  useEffect(() => {
    if (add_vendor_status === asyncStatus.SUCCEEDED) {
      dispatch(setAddVendorStatus());
      if (formikRef.current) formikRef.current.resetForm();
      navigate("/vendor");
    }
  }, [add_vendor_status]);

  useEffect(() => {
    if (update_user_status === asyncStatus.SUCCEEDED) {
      dispatch(setAsyncStatusIDLE());
      navigate("/vendor");
    }
  }, [update_user_status]);

  const _handleSubmit = (values) => {
    const payload = {
      first_name: values.first_name,
      last_name: values.last_name,
      email: values.email,
      phone: values.phone,
      services: values.services.map((data) => data.value),
      cities: values.cities.map((data) => data.value),
    };

    if (!isEditMode) {
      payload.password = values.password;
      dispatch(addVendorAsync(payload));
    } else {
      dispatch(updateUserByIdAsync({ id, data: payload }));
    }
  };

  const getValidationSchema = () => {
    return isEditMode
      ? vendorValidationsSchema("vendorEdit")
      : vendorValidationsSchema("signup");
  };

  const inputStyle = {
    borderRadius: "10px",
    border: "1px solid #e0e0e0",
    boxShadow: "none",
    fontSize: "14px",
  };

  return (
    <Stack sx={{ bgcolor: "#F8F9FA", minHeight: "100vh" }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        gap={2}
        sx={{ px: 3, py: 2.5 }}
      >
        <Box
          onClick={() => navigate("/vendor")}
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            bgcolor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            border: "1px solid #f0f0f0",
            transition: "all 0.15s",
            "&:hover": { borderColor: "#ddd", bgcolor: "#f5f5f5" },
          }}
        >
          <IoArrowBack size={16} color="#555" />
        </Box>
        <Stack>
          <Typography sx={{ fontSize: "24px", fontWeight: 700, color: themeBlue }}>
            {isEditMode ? "Edit Pro" : "Add Pro"}
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "#999", mt: 0.3 }}>
            {isEditMode ? "Update pro information" : "Fill in the details to add a new pro"}
          </Typography>
        </Stack>
      </Stack>

      {/* Form */}
      <Container sx={{ maxWidth: { xl: "md", lg: "md", md: "md", sm: "sm", xs: "xs" } }}>
        <Stack
          sx={{
            bgcolor: "#fff",
            borderRadius: "14px",
            boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
            p: { xs: 3, sm: 4 },
            mb: 4,
          }}
        >
          <Formik
            initialValues={initialValues}
            validationSchema={getValidationSchema()}
            onSubmit={_handleSubmit}
            innerRef={formikRef}
            enableReinitialize={true}
          >
            {({ errors, touched, setFieldValue, values, handleSubmit }) => (
              <Form>
                <Stack gap={3}>
                  {/* Personal Info */}
                  <Stack gap={0.5}>
                    <Typography sx={{ fontSize: "15px", fontWeight: 700, color: themeBlue }}>
                      Personal Information
                    </Typography>
                    <Box sx={{ height: "1px", bgcolor: "#f0f0f0", mt: 1 }} />
                  </Stack>

                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>
                      <Stack gap={0.5}>
                        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#555" }}>
                          First Name
                        </Typography>
                        <Input
                          id="first_name"
                          type="text"
                          onChange={(e) => setFieldValue("first_name", e.target.value)}
                          style={inputStyle}
                          placeholder="Enter first name"
                          value={values.first_name}
                        />
                        {errors.first_name && touched.first_name && (
                          <Typography sx={{ color: "#C62828", fontSize: "12px" }}>
                            {errors.first_name}
                          </Typography>
                        )}
                      </Stack>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Stack gap={0.5}>
                        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#555" }}>
                          Last Name
                        </Typography>
                        <Input
                          id="last_name"
                          type="text"
                          onChange={(e) => setFieldValue("last_name", e.target.value)}
                          style={inputStyle}
                          placeholder="Enter last name"
                          value={values.last_name}
                        />
                        {errors.last_name && touched.last_name && (
                          <Typography sx={{ color: "#C62828", fontSize: "12px" }}>
                            {errors.last_name}
                          </Typography>
                        )}
                      </Stack>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Stack gap={0.5}>
                        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#555" }}>
                          Email
                        </Typography>
                        <Input
                          id="email"
                          type="email"
                          onChange={(e) => setFieldValue("email", e.target.value)}
                          style={inputStyle}
                          placeholder="Enter email address"
                          value={values.email}
                        />
                        {errors.email && touched.email && (
                          <Typography sx={{ color: "#C62828", fontSize: "12px" }}>
                            {errors.email}
                          </Typography>
                        )}
                      </Stack>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Stack gap={0.5}>
                        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#555" }}>
                          Phone Number
                        </Typography>
                        <Input
                          id="phone"
                          type="text"
                          onChange={(e) => setFieldValue("phone", e.target.value)}
                          style={inputStyle}
                          placeholder="Enter phone number"
                          value={values.phone}
                        />
                        {errors.phone && touched.phone && (
                          <Typography sx={{ color: "#C62828", fontSize: "12px" }}>
                            {errors.phone}
                          </Typography>
                        )}
                      </Stack>
                    </Grid>
                  </Grid>

                  {/* Password - only in add mode */}
                  {!isEditMode && (
                    <>
                      <Stack gap={0.5} mt={1}>
                        <Typography sx={{ fontSize: "15px", fontWeight: 700, color: themeBlue }}>
                          Password
                        </Typography>
                        <Box sx={{ height: "1px", bgcolor: "#f0f0f0", mt: 1 }} />
                      </Stack>

                      <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6}>
                          <Stack gap={0.5}>
                            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#555" }}>
                              Password
                            </Typography>
                            <Stack position="relative">
                              <Input
                                id="password"
                                type={showPass.password ? "text" : "password"}
                                onChange={(e) => setFieldValue("password", e.target.value)}
                                style={inputStyle}
                                placeholder="Enter password"
                                value={values.password}
                              />
                              <span
                                onClick={() => setShowPass({ ...showPass, password: !showPass.password })}
                                style={{
                                  position: "absolute",
                                  top: 14,
                                  right: 12,
                                  cursor: "pointer",
                                  color: "#999",
                                }}
                              >
                                {showPass.password ? <BsFillEyeFill size={16} /> : <BsFillEyeSlashFill size={16} />}
                              </span>
                            </Stack>
                            {errors.password && touched.password && (
                              <Typography sx={{ color: "#C62828", fontSize: "12px" }}>
                                {errors.password}
                              </Typography>
                            )}
                          </Stack>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Stack gap={0.5}>
                            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#555" }}>
                              Confirm Password
                            </Typography>
                            <Stack position="relative">
                              <Input
                                id="confirmPassword"
                                type={showPass.confirmPassword ? "text" : "password"}
                                onChange={(e) => setFieldValue("confirmPassword", e.target.value)}
                                style={inputStyle}
                                placeholder="Confirm password"
                                value={values.confirmPassword}
                              />
                              <span
                                onClick={() => setShowPass({ ...showPass, confirmPassword: !showPass.confirmPassword })}
                                style={{
                                  position: "absolute",
                                  top: 14,
                                  right: 12,
                                  cursor: "pointer",
                                  color: "#999",
                                }}
                              >
                                {showPass.confirmPassword ? <BsFillEyeFill size={16} /> : <BsFillEyeSlashFill size={16} />}
                              </span>
                            </Stack>
                            {errors.confirmPassword && touched.confirmPassword && (
                              <Typography sx={{ color: "#C62828", fontSize: "12px" }}>
                                {errors.confirmPassword}
                              </Typography>
                            )}
                          </Stack>
                        </Grid>
                      </Grid>
                    </>
                  )}

                  {/* Services & Areas */}
                  <Stack gap={0.5} mt={1}>
                    <Typography sx={{ fontSize: "15px", fontWeight: 700, color: themeBlue }}>
                      Services & Coverage
                    </Typography>
                    <Box sx={{ height: "1px", bgcolor: "#f0f0f0", mt: 1 }} />
                  </Stack>

                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>
                      <Stack gap={0.5}>
                        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#555" }}>
                          Services
                        </Typography>
                        <CustomSelectBox
                          options={
                            get_services_for_signup_data?.services?.map((data) => ({
                              value: data?.id,
                              label: data.name,
                            })) || []
                          }
                          isMulti={true}
                          value={values.services}
                          onChange={(e) => setFieldValue("services", e)}
                          placeholder="Select services"
                          customStyles={{
                            control: {
                              borderRadius: "10px",
                              height: "auto",
                              minHeight: "46px",
                              width: "100%",
                              borderColor: "#e0e0e0",
                            },
                          }}
                        />
                        {errors.services && touched.services && (
                          <Typography sx={{ color: "#C62828", fontSize: "12px" }}>
                            {errors.services}
                          </Typography>
                        )}
                      </Stack>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Stack gap={0.5}>
                        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#555" }}>
                          Service Area
                        </Typography>
                        <CustomSelectBox
                          options={
                            get_services_for_signup_data?.cities?.map((data) => ({
                              value: data?.id,
                              label: data.name,
                            })) || []
                          }
                          isMulti={true}
                          value={values.cities}
                          onChange={(e) => setFieldValue("cities", e)}
                          placeholder="Select cities"
                          customStyles={{
                            control: {
                              borderRadius: "10px",
                              height: "auto",
                              minHeight: "46px",
                              width: "100%",
                              borderColor: "#e0e0e0",
                            },
                          }}
                        />
                        {errors.cities && touched.cities && (
                          <Typography sx={{ color: "#C62828", fontSize: "12px" }}>
                            {errors.cities}
                          </Typography>
                        )}
                      </Stack>
                    </Grid>
                  </Grid>

                  {/* Submit */}
                  <Stack direction="row" justifyContent="flex-end" mt={2}>
                    <Box
                      onClick={() => navigate("/vendor")}
                      sx={{
                        px: 3,
                        py: 1.3,
                        borderRadius: "10px",
                        border: "1px solid #e0e0e0",
                        cursor: "pointer",
                        mr: 1.5,
                        transition: "all 0.15s",
                        "&:hover": { bgcolor: "#f5f5f5" },
                      }}
                    >
                      <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#666" }}>
                        Cancel
                      </Typography>
                    </Box>
                    <Box
                      onClick={handleSubmit}
                      sx={{
                        px: 4,
                        py: 1.3,
                        borderRadius: "10px",
                        background: "linear-gradient(135deg, #F15A24 0%, #C53F10 100%)",
                        cursor: isLoading ? "not-allowed" : "pointer",
                        opacity: isLoading ? 0.7 : 1,
                        transition: "all 0.2s",
                        "&:hover": { transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(241,90,36,0.35)" },
                      }}
                    >
                      {isLoading ? (
                        <CircularProgress size={18} sx={{ color: "#fff" }} />
                      ) : (
                        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#fff" }}>
                          {isEditMode ? "Update Pro" : "Add Pro"}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Stack>
              </Form>
            )}
          </Formik>
        </Stack>
      </Container>
    </Stack>
  );
};

export default AddVendor;
